import {
    asString,
    loadedDefinedMapWithDefault,
    asBoolean,
    loadedDefinedMap,
} from "common/components/component-helpers";
import { LOADING, ActionSource } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { Appearance } from "common/generator/components/button-appearance";
import type { ConfirmProps } from "../ui/views/button/button-view-types";
import { definedMapWithDefault } from "common/Support";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import Button from "../ui/smart/button/button";
import { ConfirmModalStyle } from "common/components/confirm-modal";
import type { Icon } from "common/components/icons";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useForce } from "hooks/use-force";
import { Mood } from "common/generator/components/mood";

export const ButtonMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            titleValue,
            appearanceValue,
            floatingIconValue,
            onTap,
            confirm,
            confirmTitle,
            confirmDesc,
            confirmStyle,
            acceptText,
            cancelText,
            moodValue,
            nonBlockingValue,
        ],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const nonBlocking = loadedDefinedMap(nonBlockingValue, asBoolean) ?? false;
    const { onClick, actionIsVisible, targetURL, busy } = useOnClickFromAction(
        onTap,
        ActionSource.Button,
        ctx,
        env,
        !nonBlocking
    );

    if (!actionIsVisible) return null;

    if (titleValue === LOADING || confirm === LOADING || onTap === LOADING) {
        return env.renderProgress();
    }

    const appearance = loadedDefinedMapWithDefault(appearanceValue, Appearance.Filled, asString) as Appearance;
    const mood = loadedDefinedMapWithDefault(moodValue, Mood.Default, asString) as Mood;
    const floatingIcon = loadedDefinedMap(floatingIconValue, asString) as Icon | undefined;
    const title = definedMapWithDefault(titleValue, "", asString);

    let confirmObj: ConfirmProps | undefined;
    if (loadedDefinedMapWithDefault(confirm, false, asBoolean)) {
        confirmObj = {
            title: loadedDefinedMapWithDefault(confirmTitle, "LOADING", asString),
            description: loadedDefinedMapWithDefault(confirmDesc, "LOADING", asString),
            modalStyle: loadedDefinedMapWithDefault(
                confirmStyle,
                ConfirmModalStyle.Confirm,
                asString
            ) as ConfirmModalStyle,
            accept: loadedDefinedMapWithDefault(acceptText, "Accept", asString),
            cancel: loadedDefinedMapWithDefault(cancelText, "Cancel", asString),
        };
    }

    if (env.omitOverlays && appearance === Appearance.Floating) {
        return null;
    }

    return (
        <Button
            title={title}
            url={targetURL}
            floatingIcon={floatingIcon}
            appearance={appearance}
            onClick={onClick}
            disabled={onClick === undefined}
            confirm={confirmObj}
            busy={busy}
            mood={mood}
            appKind={env.appEnvironment.appKind}
        />
    );
};
